// CMT-40: order the bank picker («Velg andre blodbank») the way sysadmin
// shows the user's grants — the `admins/{uid}.access` array in document
// order — instead of the ID-token claim key order (which depends on who last
// wrote the claims: sysadmin keeps insertion order, the claims backfill wrote
// them sorted by bank id).
//
// Claims stay the ONLY authorization source. This helper never adds or
// removes a bank and never touches roles: the admins doc only decides ORDER.

const compareByName = (a, b) =>
  String(a.bloodbankName || '').localeCompare(String(b.bloodbankName || ''), 'nb');

/**
 * @param {Array<{bloodbankId: string, bloodbankName?: string, role: string}>} banks
 *   the claims-derived bank set (output of prepareBloodbanks)
 * @param {Array<{bloodbankId?: string, hfId?: string, role?: string, name?: string}>} access
 *   the `admins/{uid}.access` array; legacy entries may lack role/name
 * @param {Object<string, string>} bankHfIds  {bloodbankId: hfId} for banks
 *   granted through an HF-level entry (from the bloodbank docs' `reference`)
 * @returns the SAME bank objects, ordered by the position of their
 *   bloodbankId in `access`. A bank granted via an HF entry takes that entry's
 *   position (siblings under one HF entry sort by name); an explicit bank
 *   entry wins over an HF entry. Banks absent from `access` go last, by name.
 *   Empty/missing `access` → the input order, unchanged.
 */
export const orderBanksByAccess = (banks, access, bankHfIds = {}) => {
  if (!Array.isArray(banks)) return [];
  if (!Array.isArray(access) || access.length === 0) return banks.slice();

  const bankPositions = new Map();
  const hfPositions = new Map();
  access.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;
    if (entry.bloodbankId) {
      if (!bankPositions.has(entry.bloodbankId)) bankPositions.set(entry.bloodbankId, index);
    } else if (entry.hfId) {
      if (!hfPositions.has(entry.hfId)) hfPositions.set(entry.hfId, index);
    }
  });
  // No usable entry at all: treat like a missing doc, keep the incoming order.
  if (bankPositions.size === 0 && hfPositions.size === 0) return banks.slice();

  const keyed = banks.map((bank, originalIndex) => {
    let position = bank ? bankPositions.get(bank.bloodbankId) : undefined;
    if (position === undefined && bank) {
      const hfId = (bankHfIds || {})[bank.bloodbankId];
      if (hfId !== undefined && hfPositions.has(hfId)) position = hfPositions.get(hfId);
    }
    return { bank, position: position === undefined ? Infinity : position, originalIndex };
  });

  keyed.sort((a, b) => {
    if (a.position !== b.position) return a.position < b.position ? -1 : 1;
    // Same position = same HF entry, or both absent from `access`: by name,
    // then keep the incoming order (stable) for equal names.
    return compareByName(a.bank || {}, b.bank || {}) || a.originalIndex - b.originalIndex;
  });

  return keyed.map(({ bank }) => bank);
};
